import { ArrowUpRight } from "lucide-react";
import useReveal from "../hooks/useReveal";

const cards = [
    {
        kicker: "01",
        title: "Manejo Integral de la Obesidad",
        subtitle: "Cirugía bariátrica · Uso de GLP-1",
        body: "Abordaje completo, científico y personalizado para el tratamiento de la obesidad y sus comorbilidades. Combinamos protocolos farmacológicos GLP-1 con cirugía bariátrica de vanguardia.",
        image: "/specialties/01-obesidad.png",
    },
    {
        kicker: "02",
        title: "Cirugía Laparoscópica Avanzada",
        subtitle: "Mínima invasión · Alta complejidad",
        body: "Procedimientos de alta complejidad realizados con tecnología quirúrgica de vanguardia. Menor dolor postoperatorio, recuperación acelerada y resultados estéticamente superiores.",
        image: "/specialties/02-laparoscopia.png",
    },
    {
        kicker: "03",
        title: "Enfermedades del Hígado y Vesícula Biliar",
        subtitle: "Cirugía hepatobiliar",
        body: "Diagnóstico y tratamiento quirúrgico de litiasis vesicular, colecistitis, pólipos vesiculares y patología hepática benigna mediante técnicas laparoscópicas seguras.",
        image: "/specialties/03-higado-vesicula.png",
    },
    {
        kicker: "04",
        title: "Apendicitis Aguda y Urgencias Quirúrgicas",
        subtitle: "Atención inmediata 24/7",
        body: "Manejo experto de apendicitis aguda y otras urgencias abdominales con abordaje laparoscópico cuando está indicado. Diagnóstico rápido, intervención oportuna, recuperación más corta.",
        image: "/specialties/04-apendicitis.png",
    },
    {
        kicker: "05",
        title: "Reparación de Hernias por Laparoscopia",
        subtitle: "Hernias inguinales · umbilicales · ventrales",
        body: "Reparación de hernias mediante técnicas laparoscópicas con malla protésica de última generación. Mínima cicatriz, menor recurrencia y reincorporación rápida a tus actividades.",
        image: "/specialties/05-hernias.png",
    },
    {
        kicker: "06",
        title: "Acalasia y Trastornos Motores del Esófago",
        subtitle: "Miotomía de Heller · Funduplicatura",
        body: "Manejo quirúrgico especializado de acalasia y trastornos motores esofágicos con técnicas laparoscópicas de alta precisión. Restauramos la calidad de vida y la función deglutoria.",
        image: "/specialties/06-acalasia.png",
    },
];

const SpecialtyCard = ({ kicker, title, subtitle, body, image, index }) => {
    const ref = useReveal();
    return (
        <article
            ref={ref}
            data-testid={`especialidad-card-${index}`}
            className="reveal group relative bg-navy text-cream border-t-2 border-gold overflow-hidden flex flex-col"
            style={{ transitionDelay: `${(index % 3) * 100}ms` }}
        >
            <div className="absolute top-2 left-0 right-0 h-px bg-gold/20" />

            {/* Image */}
            <div className="relative aspect-[4/3] overflow-hidden bg-navy-deep border-b border-gold/15">
                <img
                    src={image}
                    alt={title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent pointer-events-none" />
                <span className="absolute top-4 left-4 font-display text-gold/80 text-xl leading-none">
                    {kicker}
                </span>
                <span className="absolute top-4 right-4 w-9 h-9 border border-gold/40 flex items-center justify-center text-gold/80 group-hover:bg-gold group-hover:text-navy transition-all duration-500">
                    <ArrowUpRight size={16} strokeWidth={1.5} />
                </span>
            </div>

            {/* Copy */}
            <div className="p-7 lg:p-8 flex flex-col flex-1">
                <h3 className="font-display text-2xl lg:text-[1.65rem] leading-tight text-cream">
                    {title}
                </h3>
                <p className="mt-2 text-[10.5px] tracking-[0.22em] uppercase font-medium text-gold">
                    {subtitle}
                </p>
                <p className="mt-4 text-[14px] leading-relaxed text-cream/75 font-light">
                    {body}
                </p>
                <div className="mt-6 pt-4 border-t border-gold/15">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-cream/40">
                        Consulta personalizada
                    </span>
                </div>
            </div>

            <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-br from-gold/[0.04] to-transparent" />
        </article>
    );
};

const Especialidades = () => {
    const headRef = useReveal();
    return (
        <section
            id="especialidades"
            data-testid="especialidades-section"
            className="relative py-24 lg:py-32 bg-cream-warm"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div ref={headRef} className="reveal max-w-3xl mb-16 lg:mb-20">
                    <div className="flex items-center gap-4 mb-6">
                        <span className="block w-16 h-px bg-gold" />
                        <span className="eyebrow">Especialidades</span>
                    </div>
                    <h2
                        data-testid="especialidades-heading"
                        className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-navy"
                    >
                        Especialidades diseñadas para{" "}
                        <span className="italic text-navy/85">tu bienestar.</span>
                    </h2>
                    <p className="mt-6 text-ink/70 text-[15px] leading-relaxed font-light max-w-2xl">
                        Cada procedimiento se planea y ejecuta con el mismo rigor: diagnóstico
                        preciso, técnica quirúrgica de alto nivel y acompañamiento postoperatorio
                        cercano.
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {cards.map((c, i) => (
                        <SpecialtyCard key={c.title} index={i} {...c} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Especialidades;
